interface VoucherDefinition {
  code: string;
  discount: number;
}

interface PaywallPriceDefinition {
  id: string;
  one_time_price_id?: string;
  recurring_price_id?: string;
  discount_id?: string;
}

interface PaywallOptionDefinition {
  price: string;
}

interface PaywallViewDefinition {
  id: string;
  voucher?: VoucherDefinition;
  options: PaywallOptionDefinition[];
}

interface PaywallDefinition {
  id: string;
  default_view: string;
  prices: PaywallPriceDefinition[];
  views: PaywallViewDefinition[];
}

interface PaywallConfigDefinition {
  default_paywall: string;
  paywalls: PaywallDefinition[];
}

export interface VoucherOffer {
  code: string;
  discount: number;
}

interface DiscountedPricePreview {
  hasDiscount?: boolean;
  discountId?: string;
  discountPercentage?: number;
  cents?: number;
  originalCents?: number;
  subtotalCents?: number;
  originalSubtotalCents?: number;
}

export interface VoucherPricingLookup {
  resolvePriceId(key: string): string;
  getPriceData(priceId: string, discountId: string): DiscountedPricePreview | null;
}

/** Format the display code declared by the active, Paddle-backed paywall view. */
export function formatVoucherCode(template: string, date = new Date()): string {
  const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  return template
    .replace('${month_3_letter_uppercase}', month)
    .replace('${year}', String(date.getFullYear()));
}

/**
 * Resolve the initial configured offer without inventing a discount or code.
 * The code is a display label; Paddle still applies the real configured discount ID.
 */
export function getConfiguredVoucherOffer(
  config: PaywallConfigDefinition,
  paywallId?: string,
  date = new Date()
): VoucherOffer | null {
  const resolvedPaywallId = paywallId || config.default_paywall;
  const paywall = config.paywalls.find((item) => item.id === resolvedPaywallId);
  const view = paywall?.views.find((item) => item.id === paywall.default_view);
  if (!view?.voucher) return null;

  return {
    code: formatVoucherCode(view.voucher.code, date),
    discount: view.voucher.discount,
  };
}

function previewConfirmsDiscount(
  preview: DiscountedPricePreview | null,
  discountId: string,
  displayedDiscount: number
): boolean {
  if (
    !preview?.hasDiscount
    || preview.discountId !== discountId
    || Math.round(Number(preview.discountPercentage)) !== displayedDiscount
  ) {
    return false;
  }

  const current = Number(preview.subtotalCents ?? preview.cents);
  const original = Number(preview.originalSubtotalCents ?? preview.originalCents);
  return Number.isFinite(current) && Number.isFinite(original) && current >= 0 && current < original;
}

/**
 * Confirm that every option in the active view has a discounted Paddle preview for the
 * amount due now. Paired trial products intentionally keep their future recurring renewal
 * at full price, so the one-time item is authoritative when it exists.
 */
export function isConfiguredVoucherPricingVerified(
  config: PaywallConfigDefinition,
  paywallId: string | undefined,
  lookup: VoucherPricingLookup,
  viewId?: string
): boolean {
  const resolvedPaywallId = paywallId || config.default_paywall;
  const paywall = config.paywalls.find((item) => item.id === resolvedPaywallId);
  const resolvedViewId = viewId || paywall?.default_view;
  const view = paywall?.views.find((item) => item.id === resolvedViewId);
  if (!paywall || !view?.voucher || view.options.length === 0) return false;

  const prices = new Map(paywall.prices.map((price) => [price.id, price]));
  return view.options.every((option) => {
    const price = prices.get(option.price);
    if (!price?.discount_id) return false;

    const discountId = lookup.resolvePriceId(price.discount_id);
    const dueNowPriceKey = price.one_time_price_id || price.recurring_price_id;
    if (!dueNowPriceKey) return false;

    const dueNowPriceId = lookup.resolvePriceId(dueNowPriceKey);
    return previewConfirmsDiscount(
      lookup.getPriceData(dueNowPriceId, discountId),
      discountId,
      view.voucher!.discount
    );
  });
}
